import { useEffect, useRef, useState } from 'react'

// Rounded pill CTA used across the landing page. `variant` picks the storefront
// button styles: neon brand gradient, cream/outline, or dark ink.
export function ShopButton({ variant = 'primary', href, children, className = '', ...props }) {
  // primary/secondary carry the Shopify original's soft "inflated" shading:
  // a light source top-left (inset highlight), a deeper rim (inset shadow at
  // the bottom), and a soft drop glow underneath.
  const variants = {
    primary:
      'bg-[radial-gradient(115%_140%_at_32%_20%,#f7ff94_0%,#EBFF59_45%,#beef7c_100%)] text-[#292237] hover:brightness-105 shadow-[inset_0_2px_6px_rgba(255,255,255,0.8),inset_0_-10px_18px_rgba(122,168,32,0.35),0_12px_30px_-8px_rgba(198,224,60,0.8)]',
    secondary:
      'bg-[linear-gradient(180deg,#ffffff_0%,#f3f0f5_100%)] text-[#292237] border border-[#292237]/8 hover:text-[#460069] shadow-[inset_0_2px_4px_rgba(255,255,255,0.9),inset_0_-8px_14px_rgba(41,34,55,0.07),0_12px_26px_-12px_rgba(41,34,55,0.35)]',
    ink: 'bg-[#292237] text-white hover:bg-[#181521] shadow-[0_10px_24px_-8px_rgba(25,21,34,0.5)]',
    purple:
      'bg-[#460069] text-white hover:bg-[#5a1a80] shadow-[inset_0_2px_5px_rgba(255,255,255,0.22),0_14px_30px_-10px_rgba(70,0,105,0.65)]',
    lemon: 'bg-[#EBFF59] text-[#292237] hover:bg-[#e0f542] shadow-[0_10px_24px_-8px_rgba(200,224,0,0.5)]',
  }
  const base =
    'inline-flex items-center justify-center rounded-full px-9 py-3.5 text-base font-semibold transition-all duration-200 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#460069]/40 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 disabled:active:scale-100'
  const cls = `${base} ${variants[variant]} ${className}`

  if (href) {
    const external = /^https?:/.test(href)
    return (
      <a
        href={href}
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        className={cls}
        {...props}
      >
        {children}
      </a>
    )
  }
  return (
    <button className={cls} {...props}>
      {children}
    </button>
  )
}

// Fade + rise reveal as sections scroll into view. Respects reduced motion.
export function Reveal({ children, className = '', delay = 0 }) {
  const ref = useRef(null)
  const [shown, setShown] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setShown(true)
      return
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true)
            io.unobserve(e.target)
          }
        })
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        shown ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  )
}

// Eyebrow / section label pill.
export function Eyebrow({ children, tone = 'purple' }) {
  const tones = {
    purple: 'bg-[#E5D5FF] text-[#460069]',
    mint: 'bg-[#C6FF9C] text-[#2e5a12]',
    lemon: 'bg-[#EBFF59] text-[#5c6b00]',
  }
  return (
    <span
      className={`inline-flex items-center rounded-full px-3.5 py-1 text-xs font-semibold uppercase tracking-wide ${tones[tone]}`}
    >
      {children}
    </span>
  )
}
